/**
 * Setting up one agent for callbacks: four questions, then a brief.
 *
 * ## Rebuilt from the jobs, 2026-09-04
 *
 * Arun, after the previous rebuild: *"these are prose and are jus tlisting thigns. i pressed
 * somerhing and somethign happened - now i am anxios abour waht got changed"*, and then the
 * shape he wanted: *"cant you just give me a simple checkbox for the fdes to tick?"* Then, most
 * usefully, the jobs themselves:
 *
 *   1. for an account, for one or more agents, i want to enable call back
 *   2. the agent must have call back captured … i will use conv fields to indicate call back
 *      yes and a date if avaialabel
 *   3. the code action runs post calls and pushes only the numbers that meet my criteria
 *   4. when the call back goes, can i make it talk about waht happened previosuly? … i iwll
 *      udpate th agent insgrucitons to use these fields
 *   5. i wnt to test taht this worls before i push
 *
 * So this screen is those jobs, in that order, and every row on it is exactly one of three
 * things: a control with one question in it, a fact the app already knows, or the button that
 * produces the brief. No prose that lists things — that was the complaint, and it was fair.
 * (The controls are mostly *pickers*, not ticks: Arun, on the first version, *"cant you just
 * give me a simple checkbox"*, then on the second, once every choice was a checkbox, that a
 * field is not a yes/no question. Block 1 picks fields, block 2 has the one real tick.)
 *
 * ## Two rules that make it safe to touch
 *
 *  - **Nothing here writes to the tenant.** Every default is derived from what the agent has
 *    right now, read live; the brief is what changes 8loop, when their Claude Code runs it.
 *    Three controls do write to *this app's* own database, and each says so where it sits:
 *    block 1's two field pickers (which field means what, on this agent) and block 3's value
 *    meanings. Block 2 writes nothing at all — it only shapes the brief. This distinction was
 *    worth getting right: Arun, on an earlier version, *"i pressed somerhing and somethign
 *    happened - now i am anxios abour waht got changed."*
 *  - **The app never rewrites somebody's prose.** It says which instruction a field's wording
 *    appears to be missing and hands the sentence over. equentis agent 426 is why: 1575
 *    characters of careful, better-than-ours prompt, which an earlier version of this screen
 *    offered to replace with four generic sentences.
 *
 * ## Which layer decides what — the sentence pair that must not drift
 *
 * The script decides **what is posted** (their values, their criteria, changes on reinstall).
 * This account decides **whether and when a posted call is dialled** (Outcomes, changes at
 * once, and keeps the record either way). Both sentences appear on this screen, next to the
 * thing each governs, because getting them the wrong way round is how somebody edits a script
 * to change a delay.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  CircleSlash,
  Copy,
  Download,
  FlaskConical,
  MinusCircle,
  Play,
  RefreshCw,
  Terminal,
  Upload,
  XCircle,
} from "lucide-react";
import { SettingsSection } from "@/components/ui/settings-section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  errorMessage,
  type AccountConfig,
  type CallbackAgent,
  type CodeActionInstallResponse,
  type CodeActionPlan,
  type CodeActionTestResponse,
  type OutcomeRule,
} from "@/lib/api";
import { minutesToWords } from "@/lib/labels";
import {
  useCodeActionInstall,
  useCodeActionPlan,
  useCodeActionPublish,
  useCodeActionScript,
  useCodeActionTest,
  useDeleteFieldMapping,
  useRecentCalls,
  useSaveOutcomeRules,
  useSetFieldRole,
} from "@/lib/queries";

const STEP_ICON: Record<string, React.ReactElement> = {
  done: <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />,
  would: <Circle className="text-muted-foreground mt-0.5 size-4 shrink-0" />,
  skipped: <CircleSlash className="text-muted-foreground mt-0.5 size-4 shrink-0" />,
  failed: <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />,
};

/**
 * The branches worth testing in 8loop's own sandbox, as conversation-field maps.
 *
 * Voicemail first because it is the one that needs no post-call analysis at all — a call
 * that reached an answering machine never gets a disposition, so an FDE who only tests a
 * connected call never exercises the path most callbacks come from.
 */
type TestBranch = {
  value: string;
  label: string;
  hint: string;
  fields: (plan: CodeActionPlan) => Record<string, string>;
};

const BASE_CALL = (plan: CodeActionPlan): Record<string, string> => ({
  call__id: `sandbox-${Date.now()}`,
  call__status: "ENDED",
  call__user_number: "+919000000001",
  agent__id: plan.agent.id,
  agent__name: plan.agent.name ?? "",
  tenant__domain: plan.account.tenant_host,
  ...(plan.channel ? { call__agent_channel_id: plan.channel.id } : {}),
  ...(plan.channel?.routing_identifier
    ? { system__routing_identifier: plan.channel.routing_identifier }
    : {}),
});

const TEST_BRANCHES: TestBranch[] = [
  {
    value: "voicemail",
    label: "Reached voicemail",
    hint: "decided from the end reason alone — this call never ran post-call analysis",
    fields: (plan) => ({ ...BASE_CALL(plan), call__end_reason: "VOICEMAIL" }),
  },
  {
    value: "no_answer",
    label: "Nobody answered",
    hint: "the other branch that needs no disposition at all",
    fields: (plan) => ({ ...BASE_CALL(plan), call__end_reason: "NO_ANSWER" }),
  },
  {
    value: "connected_callback",
    label: "Connected, asked for a callback at a set time",
    hint: "needs the disposition and callback-time fields to be right",
    fields: (plan) => ({
      ...BASE_CALL(plan),
      call__end_reason: "USER_HANGUP",
      ...(dispositionName(plan) ? { [dispositionName(plan)!]: callbackValue(plan) } : {}),
      ...(callbackTimeName(plan) ? { [callbackTimeName(plan)!]: isoInHours(3) } : {}),
    }),
  },
  {
    value: "connected_nothing",
    label: "Connected, and nothing was agreed",
    hint: "must decide to send nothing — the commonest call of all",
    fields: (plan) => ({ ...BASE_CALL(plan), call__end_reason: "USER_HANGUP" }),
  },
  {
    value: "not_ended",
    label: "A call still in progress",
    hint: "must do nothing at all, and reaches no network",
    fields: (plan) => ({ ...BASE_CALL(plan), call__status: "IN_PROGRESS", call__end_reason: "" }),
  },
];

const dispositionName = (plan: CodeActionPlan): string | null =>
  plan.roles.disposition?.field.name ?? null;
const callbackTimeName = (plan: CodeActionPlan): string | null =>
  plan.roles.callback_time?.field.name ?? null;

/** A value on this agent's own disposition field that means "call me back". */
function callbackValue(plan: CodeActionPlan): string {
  const mapped = plan.disposition_map.find((m) => m.outcome === "callback_requested");
  if (mapped) return mapped.value;
  // Free-text disposition: the script matches phrases, so send one.
  return "Customer asked us to call back";
}

function isoInHours(h: number): string {
  return new Date(Date.now() + h * 3600_000).toISOString();
}

/**
 * Whether 8loop could actually reach the URL the script would post to.
 *
 * This is the difference between an install that works and one that looks perfect and
 * delivers nothing, and it is worth deciding in the client rather than only reading it out
 * of `plan.warnings`: the sandbox and the live webhook both fail on a private destination,
 * so two separate controls need the same answer and need to give the same reason.
 *
 * 8loop refuses loopback, private-range and `.local` destinations outright with
 * `SsrfBlockedError` — a policy refusal, not a network timeout, so no amount of local
 * tunnelling changes it.
 */
function reachableFrom8loop(ingestUrl: string): boolean {
  try {
    const { protocol, hostname } = new URL(ingestUrl);
    if (protocol !== "https:") return false;
    if (hostname === "localhost" || hostname.endsWith(".local")) return false;
    if (/^127\.|^0\.|^10\.|^192\.168\.|^169\.254\./.test(hostname)) return false;
    if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether this page offers to install the hook itself.
 *
 * Off, at Arun's request: *"maybe we dont need the let app install it for now. - thats what is
 * confusing a lot here i feel (hide it from ui for now - dont remove the code yeta)"*. Two routes
 * to one outcome is exactly the "pick one — you do not need both" problem that this page has
 * been through twice, and the brief is the route he wants. Everything behind this flag still
 * works — `POST /install`, `POST /publish`, the dry run, the transcript renderer — and flipping
 * it back is one line.
 */
const SHOW_APP_INSTALL = false;

/** Endings the line itself reports, which need no field on the agent at all. */
const LINE_ENDINGS: { outcome: string; label: string; meaning: string }[] = [
  {
    outcome: "voicemail",
    label: "Reached voicemail",
    meaning:
      "8loop reports it on the call, so no field is needed — and no disposition ever exists for it, because a call that never connected skips post-call analysis.",
  },
  {
    outcome: "no_answer",
    label: "Nobody picked up",
    meaning:
      "Includes a busy line: 8loop cannot tell a busy tone from a ring-out, and has no busy reason at all.",
  },
  {
    outcome: "never_spoke",
    label: "Answered, but nobody spoke",
    /*
      Arun's correction, and he is right: *"screener ends by giving to voicemail so end reasion =
      vociemail or echo can be retried"*. A call screener that hands off arrives as `VOICEMAIL`
      and is caught by the row above, so naming it here pointed at the wrong row. What actually
      lands here is `USER_SILENCE` and `ECHO_DETECTED` — the line opened and no human ever spoke.
    */
    meaning:
      "The line opened and nobody ever spoke: silence, or an echo on the trunk. A screener that hands off to an answering machine is not this — that arrives as voicemail, above.",
  },
];

export function CodeActionSection({
  accountId,
  config,
  agent,
}: {
  accountId: string;
  config: AccountConfig;
  /** The row from the account's callback list. This section is always about one agent. */
  agent: CallbackAgent;
}): React.ReactElement {
  const chosen = agent.agent_id;

  const planQuery = useCodeActionPlan(accountId, chosen);
  const plan = planQuery.data?.plan;

  const script = useCodeActionScript(accountId);
  const installer = useCodeActionInstall(accountId);
  const publisher = useCodeActionPublish(accountId);
  const setRole = useSetFieldRole(accountId);
  const saveRules = useSaveOutcomeRules(accountId);
  const tester = useCodeActionTest(accountId);

  const [confirmPublish, setConfirmPublish] = useState(false);
  const [install, setInstall] = useState<CodeActionInstallResponse | null>(null);
  const [published, setPublished] = useState<CodeActionInstallResponse | null>(null);
  const [testRun, setTestRun] = useState<CodeActionTestResponse | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testAgainst, setTestAgainst] = useState<string>("");

  // ---------------------------------------------------------------- the ticks ---
  //
  // Every default is derived from the live plan, and a tick shapes the brief and nothing else.
  // Nothing is stored, which is what makes the card's promise true: ticking a box here cannot
  // have changed anything on the tenant, because nothing but the brief reads it.

  const hasTime = Boolean(plan?.roles.callback_time);

  const [mergeWording, setMergeWording] = useState(true);
  /**
   * The one question in block 2, and it defaults to **on**.
   *
   * Arun: *"righy now - Create callback_context — why this call is happening is selected and cant
   * change. wat if i dont want this"*. So it is a real checkbox. On by default all the same,
   * because defaulting it off would ship cold callbacks silently for every new agent — and
   * unticking is one click, whereas discovering that your callbacks open with "who is this?" takes
   * a customer complaint.
   */
  const [wantContext, setWantContext] = useState(true);
  /** Existing fields the callback should hear, keyed by name. Nothing is pre-picked. */
  const [passBack, setPassBack] = useState<Record<string, boolean>>({});
  /** Fields that do not exist yet, for the brief to have Claude Code create. */
  const [newFields, setNewFields] = useState<string[]>([]);
  const [newFieldDraft, setNewFieldDraft] = useState("");

  /** Fields post-call analysis writes: the only candidates for "what happened last time". */
  const capturedFields = useMemo(
    () =>
      (plan?.fields ?? []).filter(
        (f) =>
          f.re_extract_on_conversation_end &&
          !f.set_on_conversation_start &&
          f.name !== plan?.roles.callback_time?.field.name,
      ),
    [plan],
  );

  /** Fields that could hold a callback time: written at hang-up, and a type that can hold one. */
  const timeCandidates = useMemo(
    () =>
      (plan?.fields ?? []).filter(
        (f) =>
          // 8loop returns archived fields in the list, and one offered here would be picked,
          // written into the script, and never filled by anything.
          !f.archived &&
          f.re_extract_on_conversation_end &&
          ["STRING", "DATE", "DATETIME"].includes((f.type ?? "STRING").toUpperCase()),
      ),
    [plan],
  );

  const settableNames = useMemo(
    () => new Set(
      (plan?.fields ?? [])
        .filter((f) => !f.archived && f.set_on_conversation_start)
        .map((f) => f.name),
    ),
    [plan],
  );

  const contextFor = useMemo(
    () => Object.entries(passBack).filter(([, on]) => on).map(([name]) => name),
    [passBack],
  );

  const target = {
    agent_id: chosen,
    // The brief generates as though the field it is about to ask for already exists, so the
    // script it carries names it. Nothing is created by this app either way.
    //
    // Only the callback-time field. A disposition field is no longer asked for at all: its one
    // remaining job was "they asked but named no hour", and the time field's own description
    // already asks for the exact phrase "requested no time given" for that case. Telling
    // somebody's Claude Code to create a second field for it was asking for work that buys
    // nothing.
    assume_created: [...(hasTime ? [] : (["callback_time"] as const))],
    context_for: wantContext ? contextFor : [],
    create_context: wantContext ? newFields : [],
    merge_wording: mergeWording,
    want_context: wantContext,
  };

  const liveOnAgent = Boolean(plan?.webhook.existing?.is_ours);
  /** Installed, correct-looking, and rejected on every call. The state nothing else can see. */
  const tokenStale = plan?.webhook.token_stale === true;
  const stagedOnDraft = Boolean(plan?.webhook.on_draft) || Boolean(install?.staged);

  /** The one human case left for the ingest token; everything else the app does itself. */
  const tokenUnrecoverable =
    config.credentials.has_ingest_token && !config.credentials.ingest_token_readable;

  const noPlanReason = planQuery.isError
    ? "This agent could not be read from 8loop — the error above says why."
    : "Still reading this agent from 8loop.";

  const handoverBlocked: string | null = !plan
    ? noPlanReason
    : tokenUnrecoverable
      ? "This account's ingest token was issued before this app kept a readable copy, and no hook installed from this app carries it. Fix it once on Configuration → Setup."
      : null;

  const publishBlocked: string | null = !plan
    ? noPlanReason
    : !plan.draft.exists
      ? liveOnAgent
        ? `Nothing to publish: the hook is already live on ${plan.agent.active_version ?? "the active version"}.`
        : "Nothing to publish yet — write the hook to the draft first."
      : !stagedOnDraft
        ? `Draft ${plan.draft.version} has no hook from this app on it, so publishing would make nothing new fire.`
        : !confirmPublish
          ? "Publishing ships everything in this draft to live callers, so it needs the confirmation below."
          : null;

  const testBlocked: string | null = !plan
    ? noPlanReason
    : handoverBlocked
      ? handoverBlocked
      : !reachableFrom8loop(plan.account.ingest_url)
        ? `8loop's sandbox cannot reach ${plan.account.ingest_url}. It refuses loopback and private addresses outright, so this can only run once the app is on a public https URL.`
        : null;

  const recent = useRecentCalls(accountId, chosen, testOpen && !testBlocked);

  // ------------------------------------------------------------------ actions ---

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      toast.error("Couldn't reach the clipboard. Use Download instead.");
    }
  };

  const withScript = async (then: (s: string, b: string) => void) => {
    try {
      const res = await script.mutateAsync(target);
      then(res.script, res.brief);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const download = (text: string, filename: string) => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runInstall = async (dryRun: boolean) => {
    setInstall(null);
    setPublished(null);
    try {
      const res = await installer.mutateAsync({
        ...target,
        // Never: the app does not write to the tenant's field schema. The brief asks, and
        // their Claude Code does it — which is also the only way the wording gets improved
        // rather than replaced.
        create_fields: [],
        use_existing_draft: true,
        publish: false,
        dry_run: dryRun,
      });
      setInstall(res);
      if (!dryRun && res.ok) toast.success("Written to the draft. It fires nothing until you publish.");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const runPublish = async () => {
    setPublished(null);
    try {
      const res = await publisher.mutateAsync({ agent_id: chosen, confirm: true });
      setPublished(res);
      setConfirmPublish(false);
      if (res.ok) toast.success("Published. The next call this agent ends comes here.");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const runTest = async () => {
    if (!plan) return;
    setTestRun(null);
    const synthetic = TEST_BRANCHES.find((b) => b.value === testAgainst);
    try {
      const res = await tester.mutateAsync({
        agent_id: chosen,
        preview: true,
        ...(synthetic ? { field_values: synthetic.fields(plan) } : { call_id: testAgainst }),
      });
      setTestRun(res);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  /** One account-level ending, switched on or off. Sends the whole set, as the endpoint wants. */
  const toggleEnding = (outcome: string, enabled: boolean) => {
    const rules = config.outcome_rules
      .filter((r) => !r.never_dialled)
      .map((r) => ({
        outcome: r.outcome,
        enabled: r.outcome === outcome ? enabled : r.enabled,
        delay_minutes: r.delay_minutes,
        retry_delay_minutes: r.retry_delay_minutes,
        max_attempts: r.max_attempts,
      }));
    saveRules.mutate(rules, {
      onSuccess: () =>
        toast.success(
          enabled
            ? `On for every agent on this account. Takes effect on the next call; nothing is reinstalled.`
            : `Off for every agent on this account. Calls that end this way are recorded and never dialled.`,
        ),
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  const ruleFor = (outcome: string): OutcomeRule | undefined =>
    config.outcome_rules.find((r) => r.outcome === outcome);
  const noTimeRule = ruleFor("callback_no_time");
  const busy = script.isPending || installer.isPending || publisher.isPending || tester.isPending;

  return (
    <div className="space-y-6">
      {/* -------------------------------------------------- where this stands --- */}
      <SettingsSection
        title="Post-call hook"
        description="One webhook on this agent, running after every call it ends."
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <p className="text-muted-foreground text-xs">
              Read live from 8loop each time this page opens.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void planQuery.refetch()}
              disabled={planQuery.isFetching}
            >
              <RefreshCw className={cn("size-3.5", planQuery.isFetching && "animate-spin")} />
              {planQuery.isFetching ? "Reading 8loop…" : "Re-read this agent"}
            </Button>
          </div>
        }
      >
        {planQuery.isLoading || !plan ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <div
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3",
              tokenStale
                ? "border-destructive/40 bg-destructive/5"
                : liveOnAgent
                  ? "border-success/40 bg-success/5"
                  : stagedOnDraft
                    ? "border-warning/40 bg-warning/5"
                    : "border-border",
            )}
          >
            {tokenStale ? (
              <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
            ) : liveOnAgent ? (
              <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />
            ) : stagedOnDraft ? (
              <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
            ) : (
              <MinusCircle className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            )}
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium">
                {tokenStale
                  ? "Installed, and every call it makes is rejected"
                  : liveOnAgent
                    ? `Live on version ${plan.agent.active_version ?? "?"}`
                    : stagedOnDraft
                      ? `On draft ${plan.draft.version ?? "?"} — written, not published`
                      : "Not installed"}
              </p>
              <p className="text-muted-foreground text-sm">
                {tokenStale
                  ? "The hook on this agent is posting with an ingest token this account no longer accepts — somebody issued a new one and this hook was never updated, so it has been getting a 401 on every call and no callback from it has arrived since. Copy the brief below and have your Claude Code redo it; that is all this needs."
                  : liveOnAgent
                    ? "Every call this agent ends is posted here. After changing anything below, hand over a fresh brief and have your Claude Code redo the install steps."
                    : stagedOnDraft
                      ? "A hook on a draft fires on no call at all — 8loop runs the webhooks belonging to the version a call ran on. Publish the agent in 8loop, or have your Claude Code do the publish step of the brief."
                      : "No finished call on this agent reaches this app yet. Set it up below, then hand the brief to your Claude Code."}
              </p>
            </div>
          </div>
        )}

        {planQuery.isError ? (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{errorMessage(planQuery.error)}</AlertDescription>
          </Alert>
        ) : null}
      </SettingsSection>

      {/* ------------------------------------------------------- 1, 2, 3 ticks --- */}
      <SettingsSection
        title="Set up this agent's hook"
        description="Answer the questions below, then hand the brief to your Claude Code. Nothing here changes the tenant — the brief does, when your Claude runs it."
      >
        {planQuery.isLoading || !plan ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <div className="space-y-7">
            {/* ------------------------------------------------------ block 1 --- */}
            <div className="space-y-3">
              {/*
                One question, and it was two. The second was a picker for a field that says
                "never call me again", under a note that opened *"You probably do not need this"*
                and then spent ninety words arguing the case. Arun: *"bro why do i need this? if i
                dont want these called, i wont even send these to the app. is this really needed?"*

                He is right about the altitude, and half right about the feature. A control whose
                own copy begins by telling you to skip it does not belong at the top of the
                screen, next to the one field the whole app depends on.

                What it actually buys is narrow, and it is not about this call: a do-not-call
                blocks the number for every *future* dial — `scheduler/dialer.ts` re-checks the
                list immediately before each callback, `scheduler/verifier.ts` re-checks it
                mid-flight, and `orchestrator/suppression.ts` gates every CRM row against the
                same table. So it matters on an account that has both a hook and a CRM feed, and
                today no account has both. It is insurance, not setup.

                So the picker moved to block 3, directly above the value rows it governs, where
                the reader can see what a value *does* instead of reading an argument about it.
                Nothing was removed: the server still accepts `dnc` and `wrong_number` from a
                hand-written hook, and the Outcomes tab still states what they do.
              */}
              <h3 className="text-sm font-semibold">1. Capture when they want the callback</h3>

              <FieldPicker
                id="role-callback-time"
                label="Field that captures the callback date and time at hang-up"
                /*
                  Two different empty states, because they need different actions.

                  The trap: pick "None of these", hand over the brief, let it create
                  `callback_date_and_time` — and the stored none keeps suppressing the guess, so
                  this card still says "no field captures a callback time" while the field sits
                  on the agent. The next brief proposes creating it again. Arun hit exactly this
                  on Ashley Furniture and I told him to click "Let the app pick again" rather
                  than fixing it, which is the wrong way round: a screen that knows the answer
                  should say it.
                */
                empty={
                  plan.roles_set_to_none.includes("callback_time") && timeCandidates.length > 0
                    ? `Set to none, but ${
                        timeCandidates.length === 1
                          ? `“${timeCandidates[0].name}” on this agent could be it`
                          : `${timeCandidates.length} fields on this agent could be it`
                      } — including anything a brief has created since. Pick one above, or:`
                    : "No field on this agent captures a callback time. The brief asks your Claude Code to add callback_date_and_time — with the wording, and what to answer in any language."
                }
                candidates={timeCandidates}
                chosen={plan.roles.callback_time?.field.name ?? null}
                deliberatelyNone={plan.roles_set_to_none.includes("callback_time")}
                why={plan.roles.callback_time?.why}
                busy={setRole.isPending}
                onChange={(name) =>
                  setRole.mutate(
                    { agent_id: chosen, role: "callback_time", field_name: name },
                    {
                      onSuccess: (r) => toast.success(r.message),
                      onError: (e) => toast.error(errorMessage(e)),
                    },
                  )
                }
              >
                {hasTime ? (
                  <PromptCoverage
                    gaps={plan.description_gaps}
                    merge={mergeWording}
                    onMerge={setMergeWording}
                  />
                ) : null}
              </FieldPicker>

            </div>

            {/* ------------------------------------------------------ block 2 --- */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">2. Tell the callback what happened last time</h3>

              <TickRow
                id="tick-want-context"
                checked={wantContext}
                onChange={setWantContext}
                label="Give the callback the previous call's context"
                detail="Without it the callback opens with “Hello, who am I speaking to?” — to somebody who asked for this call twenty minutes ago. With it: “Hi Priya, you asked us to call you back about your policy renewal.” The brief adds callback_context to this agent and one line to its prompt telling it to use it. Untick it and callbacks open cold, which suits some flows."
              />

              {wantContext ? (
                <div className="space-y-3 pl-7">
                  {capturedFields.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        Pass back fields this agent already captures
                      </p>
                      <p className="text-muted-foreground text-sm">
                        Everything it writes at hang-up already reaches this app. Tick what the
                        callback should hear; each gets a twin the agent can receive (
                        <span className="font-mono">previous_…</span>), because a field written at
                        hang-up cannot be set on a dial. The brief creates the twins and tells the
                        prompt to use whichever arrive.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {capturedFields.map((f) => {
                          const on = passBack[f.name] ?? false;
                          const already = settableNames.has(`previous_${f.name}`);
                          return (
                            <button
                              key={f.name}
                              type="button"
                              onClick={() =>
                                setPassBack((prev) => ({ ...prev, [f.name]: !on }))
                              }
                              title={
                                already
                                  ? `previous_${f.name} is already declared on this agent`
                                  : f.allowed_values?.length
                                    ? f.allowed_values.join(" · ")
                                    : undefined
                              }
                              className={cn(
                                "flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 font-mono text-xs transition-colors",
                                on
                                  ? "border-foreground bg-foreground text-background"
                                  : "hover:bg-accent border-border",
                              )}
                            >
                              {/*
                                A box, always drawn, because the off state was a bordered pill in
                                a mono font — indistinguishable from the read-only field-name tags
                                elsewhere on this page. Arun: *"its not obviosu that i can seelct
                                or needot select call disposition fidl here."* It was a real
                                button with a real hover state; nothing about it said so at rest,
                                and a control nobody recognises as a control does not exist.
                              */}
                              <span
                                aria-hidden
                                className={cn(
                                  "grid size-3.5 shrink-0 place-items-center rounded-[3px] border",
                                  on ? "border-background" : "border-muted-foreground",
                                )}
                              >
                                {on ? <Check className="size-2.5" /> : null}
                              </span>
                              {f.name}
                              {already ? " · already sent" : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      This agent captures nothing else at hang-up yet, so there is nothing existing
                      to pass back. Add one below and the brief will have it created.
                    </p>
                  )}

                  <div className="space-y-2">
                    {/*
                      Arun's framing, which is better than mine: *"instead of Or - you should ask
                      - create new field to pass back what was discussed so the agent can resume
                      from there? somethign luike this (this gives the agent a memory
                      equivalnet)"*. "New fields to capture more" describes the mechanism; "so it
                      can resume where you left off" describes what the customer experiences.
                    */}
                    <p className="text-sm font-medium">
                      Have a field created to carry what was discussed, so the callback resumes
                      where the last call stopped
                    </p>
                    <p className="text-muted-foreground text-sm">
                      This is the agent's memory of the customer. Name it and the brief has your
                      Claude Code write the extraction wording, create the field on this agent so
                      post-call analysis fills it at hang-up, and create its{" "}
                      <span className="font-mono">previous_…</span> twin so the callback is told
                      what it said. This app deliberately does not write that wording: a
                      description is the prompt the analysis reads, and a generic one produces a
                      field full of nothing.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        value={newFieldDraft}
                        onChange={(e) => setNewFieldDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          const name = newFieldDraft.trim();
                          if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) return;
                          setNewFields((prev) => (prev.includes(name) ? prev : [...prev, name]));
                          setNewFieldDraft("");
                        }}
                        placeholder="e.g. last_call_notes"
                        className="w-[280px] font-mono"
                        spellCheck={false}
                      />
                      <span className="text-muted-foreground text-xs">
                        snake_case, then Enter
                      </span>
                    </div>
                    {newFields.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {newFields.map((name) => (
                          <button
                            key={name}
                            type="button"
                            onClick={() => setNewFields((prev) => prev.filter((n) => n !== name))}
                            className="border-foreground bg-foreground text-background cursor-pointer rounded-full border px-3 py-1 font-mono text-xs"
                            title="Remove"
                          >
                            {name} ✕
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/*
                    Mappings this account made by hand, before the convention existed. Shown here
                    because this is the only place they can now be judged: the account-wide list
                    that owned them is gone, per Arun — *"felds are diofferent for doffent
                    agents"* — and a row that is quietly still being sent, or quietly dropped for
                    this agent, has to be visible somewhere.
                  */}
                  {plan.context.length > 0 ? (
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium">Also carried, from an older mapping</p>
                      <ul className="space-y-1">
                        {plan.context.map((c) => (
                          <li
                            key={c.id}
                            className="flex items-start justify-between gap-3 text-sm"
                          >
                            <span className="min-w-0">
                              <span className="font-mono">{c.field_name}</span>
                              <span className="text-muted-foreground"> — from {c.source}</span>
                              {c.settable === false ? (
                                <span className="text-warning">
                                  {" "}
                                  · this agent will not accept it, so the dial drops it and rings
                                  anyway
                                </span>
                              ) : null}
                              {c.account_wide ? (
                                <span className="text-muted-foreground">
                                  {" "}
                                  · set for every agent on this account
                                </span>
                              ) : null}
                            </span>
                            {/*
                              The only surface left that can remove one of these. The
                              account-wide Context card that created them is gone, so without
                              this a row keeps going out on every callback with nowhere to
                              judge or stop it — which is how a seeded `callback_context`
                              422'd a real callback on 2026-09-03.
                            */}
                            <StopMapping
                              accountId={accountId}
                              mapping={c}
                              onDone={() => void planQuery.refetch()}
                            />
                          </li>
                        ))}
                      </ul>
                      <p className="text-muted-foreground text-xs">
                        Set up by hand on an earlier version of this screen. Usually nothing needs
                        doing: they still work where the agent accepts the name, and are dropped
                        where it does not. The two names above carry themselves and need no
                        mapping.
                      </p>
                    </div>
                  ) : null}

                  {/*
                    His question: *"this design breaks when we do a retry on a rety or of that goes
                    to voicemail - in that case what context is held on?"* Answered plainly,
                    because the mechanism is not obvious and the honest answer is reassuring for
                    retries and worth knowing for the second callback.
                  */}
                  <p className="text-muted-foreground text-xs">
                    A retry carries the same context as the first attempt — it is rebuilt from the
                    call that asked for the callback, not from the attempt that failed. If a
                    callback is answered and they ask to be rung again, the next one carries what{" "}
                    <em>that</em> conversation captured, so the brief asks the prompt to fill these
                    fields on callbacks too.
                  </p>
                </div>
              ) : settableNames.has("callback_context") ? (
                <p className="text-warning pl-7 text-xs">
                  This agent already declares{" "}
                  <span className="font-mono">callback_context</span>, so the app still fills it in
                  on every callback. Archive the field in 8loop to stop that.
                </p>
              ) : null}
            </div>

            {/* ------------------------------------------------------ block 3 --- */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">3. Which calls come back</h3>

              {/*
                ONE list, and it has been three different shapes.

                It began as a decision widget per value of the tenant's ENUM — four actions in a
                dropdown, plus a row for every value that is *not* posted explaining that it is
                not posted — and then, underneath, a second list of switches asking whether each
                ending should be called back at all. Arun, fifth time of asking: *"why cant this
                be a checkbox instead of that dropdown? … why am i enabling these if user is
                saying i want these called back in the previosus ecgtion"*. Then, of the values
                that could never be ticked: *"why si that even na opton? eg. do not clal wrong
                number etc. dont include them in the app."*

                And finally the question that removed the rest of it: *"do i need the fields that
                sys they asked for a calback? the call back field already tells us the info for
                when to call back. the checkboxes for voceimail, nobody pikedup, answerd but no
                one spoke tells us what other scnearios to call back. what are you thinking of
                capturign with field that says they askled for a call bacK?"*

                Nothing — and the app had already been built that way without the screen
                noticing. Block 1's field answers both halves, because its own description asks
                post-call analysis for one of exactly four things:

                  an ISO datetime            → dialled then
                  "in N minutes"             → this app resolves it against the call's end
                  "requested no time given"  → the second row below decides when
                  "not applicable"           → not a callback at all

                `parseRequestedTime` has understood all four for weeks, with tests. So "did they
                ask?" and "when?" were always one field, and a second field asking the first
                question again was the same double-asking as the two lists.

                What is left is the whole of "which calls come back", and every row is
                account-wide: the time they named, the case where they named none, and the three
                endings the line itself reports. No per-agent part, so no fresh brief for a change
                here — which is why the footnote is one sentence now.
              */}

              <div className="divide-y rounded-lg border">
                {/*
                  Locked, and stated as a fact rather than drawn as a switch: a callback that
                  named a time and is not dialled at it is this whole app doing nothing.
                */}
                <div className="flex items-start gap-3 px-4 py-3">
                  <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">They named a day and time</p>
                    <p className="text-muted-foreground text-xs">
                      Always dialled, at the time they asked for — moved forward if that falls
                      outside your calling hours.
                    </p>
                  </div>
                </div>

                {noTimeRule ? (
                  <EndingTick
                    label="They asked to be called back but named no time"
                    detail="Somebody who just said “call me back”, or said “in two minutes” to an agent whose wording could not express it. This account picks the time."
                    checked={noTimeRule.enabled}
                    busy={saveRules.isPending}
                    delay={minutesToWords(
                      noTimeRule.delay_minutes ?? noTimeRule.suggested_delay_minutes,
                    )}
                    onChange={(v) => toggleEnding("callback_no_time", v)}
                  />
                ) : null}

                {LINE_ENDINGS.map((e) => {
                  const rule = ruleFor(e.outcome);
                  return (
                    <EndingTick
                      key={e.outcome}
                      label={e.label}
                      detail={e.meaning}
                      checked={rule?.enabled ?? false}
                      busy={saveRules.isPending}
                      delay={
                        rule
                          ? minutesToWords(rule.delay_minutes ?? rule.suggested_delay_minutes)
                          : null
                      }
                      onChange={(v) => toggleEnding(e.outcome, v)}
                    />
                  );
                })}
              </div>

              <p className="text-muted-foreground text-xs">
                Anything unticked is never sent to this app, so nothing here can call it — that is
                all "don't call these" means. These are{" "}
                <span className="text-foreground">account-wide</span>, shared with{" "}
                <Link
                  to={`/accounts/${accountId}/settings?tab=outcomes`}
                  className="text-foreground underline underline-offset-2"
                >
                  Configuration → Outcomes
                </Link>
                , so a change here needs no new brief — it applies to the next call. An invalid
                number always blocks itself, whatever is ticked.
              </p>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------- the handover --- */}
        <div className="space-y-3 border-t pt-5">
          <h3 className="text-sm font-semibold">Then hand it to Claude Code</h3>
          <p className="text-muted-foreground text-sm">
            One file with everything you answered above: the fields to create with exact
            bodies, the instruction to merge into this agent's own wording, the prompt change, the
            script, the install steps, and how to check it worked. Written for an agent with a
            terminal — real values throughout, read from this tenant a moment ago.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => void withScript((_s, b) => void copy(b, "Brief"))}
              disabled={busy || Boolean(handoverBlocked)}
              title={handoverBlocked ?? undefined}
            >
              <Copy className="size-4" />
              {script.isPending ? "Building it…" : "Copy the brief"}
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                void withScript((_s, b) =>
                  download(b, `callback-hook-${plan?.agent.name ?? chosen}.md`),
                )
              }
              disabled={busy || Boolean(handoverBlocked)}
              title={handoverBlocked ?? undefined}
            >
              <Download className="size-4" />
              Download it
            </Button>
            <Button
              variant="ghost"
              onClick={() => void withScript((sc) => void copy(sc, "Script"))}
              disabled={busy || Boolean(handoverBlocked)}
              title={handoverBlocked ?? undefined}
            >
              <Terminal className="size-4" />
              Just the script
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            It carries this account's ingest token — the password the hook uses to post back here.
            Don't commit the file or paste it into a ticket.
          </p>
          {handoverBlocked && plan ? (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>{handoverBlocked}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </SettingsSection>

      {/* ------------------------------------------- 4. prove it before publish --- */}
      <SettingsSection
        title="4. Prove it before you publish"
        description="Runs this exact script inside 8loop against a call that really happened, and shows what it decided, when it would dial and what the callback would carry. Nothing is dialled and nothing is published."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="test-against">Test against</Label>
            <Select
              value={testAgainst}
              onValueChange={(v) => {
                setTestAgainst(v);
                setTestRun(null);
              }}
              onOpenChange={(open) => open && setTestOpen(true)}
            >
              <SelectTrigger id="test-against" className="w-[460px]">
                <SelectValue placeholder="Pick one of this agent's finished calls" />
              </SelectTrigger>
              <SelectContent>
                {(recent.data?.calls ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.ended_at ? new Date(c.ended_at).toLocaleString() : `call ${c.id}`}
                    {/*
                      A call with no number said nothing here at all, and picking one produced a
                      422 that reads as a broken install. Agent 638 is entirely browser calls —
                      every one of its last ten had `call__user_number` null — so an FDE testing
                      the real-call path got "phone_number is required" on every attempt and had
                      to work out for themselves that the *call* was wrong, not the script.
                    */}
                    {c.number_tail
                      ? ` · …${c.number_tail}`
                      : " · browser call, no number to ring"}
                    {c.end_reason ? ` · ${c.end_reason.toLowerCase().replace(/_/g, " ")}` : ""}
                  </SelectItem>
                ))}
                {TEST_BRANCHES.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    Made-up call — {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {recent.isPending && testOpen
                ? "Reading this agent's recent calls…"
                : recent.isError
                  ? `Could not list this agent's calls (${errorMessage(recent.error)}) — the made-up ones still work.`
                  : (recent.data?.calls ?? []).length > 0 &&
                      (recent.data?.calls ?? []).every((c) => !c.number_tail)
                    ? "Every recent call on this agent is a browser call with no phone number, so replaying one answers “phone_number is required” — correctly, since there is nobody to ring. Use the made-up calls here, and a real phone call for the end-to-end check."
                    : "A real call replays the field values that conversation actually produced, which is where every failure so far has been. The made-up ones exercise a branch no recent call happens to cover."}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => void runTest()}
            disabled={busy || Boolean(testBlocked) || !testAgainst}
            title={testBlocked ?? undefined}
          >
            <Play className="size-4" />
            {tester.isPending ? "Running in 8loop…" : "Run it"}
          </Button>
        </div>

        {testBlocked ? (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              <span className="font-medium">Can't run this yet.</span> {testBlocked}
            </AlertDescription>
          </Alert>
        ) : null}

        {testRun ? <TestResult run={testRun} /> : null}

        <p className="text-muted-foreground border-t pt-4 text-xs">
          <span className="text-foreground font-medium">What this cannot prove:</span> that the
          agent's prompt actually uses the context. The fields arriving and the prompt reading them
          are two different things, and only a real call shows the second — place one after
          publishing and read its transcript. The brief ends with that step.
        </p>
      </SettingsSection>

      {/* --------------------------- or let this app install it (hidden for now) --- */}
      {SHOW_APP_INSTALL ? (
      <CollapsibleSection
        title="Or let this app install it"
        description="Same script, installed from here. Step 1 writes it onto the agent's draft; step 2 publishes, which is what makes it fire. It does not create fields or touch any wording — those come from the brief."
        summary={
          liveOnAgent
            ? "A hook from this app is live on this agent. Open this to write a fresh copy — needed after changing anything above."
            : stagedOnDraft
              ? `Written onto draft ${plan?.draft.version ?? "?"} and not published, so it fires on nothing. Open this to publish it.`
              : "Nothing installed yet. Open this to write the hook onto the agent's draft, then publish."
        }
      >
        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2">
            <Upload className="size-4" />
            <h3 className="text-sm font-semibold">Step 1 — write it to the draft</h3>
          </div>
          <p className="text-muted-foreground mt-2 text-sm">
            Nothing a caller hears changes, and you can do it as often as you like. Dry run first
            if you want to read the steps — it writes nothing at all.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => void runInstall(true)}
              disabled={busy || Boolean(handoverBlocked)}
              title={handoverBlocked ?? undefined}
            >
              <FlaskConical className="size-4" />
              Dry run
            </Button>
            <Button
              onClick={() => void runInstall(false)}
              disabled={busy || Boolean(handoverBlocked)}
              title={handoverBlocked ?? undefined}
            >
              <Upload className="size-4" />
              {installer.isPending ? "Writing to the draft…" : "Write it to the draft"}
            </Button>
          </div>
          {handoverBlocked ? (
            <p className="text-muted-foreground mt-2 text-xs">{handoverBlocked}</p>
          ) : null}
        </div>

        {install ? <InstallSteps result={install} /> : null}

        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <ArrowUpRight className="size-4" />
            <h3 className="text-sm font-semibold">Step 2 — publish the agent</h3>
            {stagedOnDraft && !liveOnAgent ? (
              <Badge variant="outline" className="text-warning">
                staged, firing nothing
              </Badge>
            ) : null}
          </div>
          <p className="text-muted-foreground text-sm">
            8loop runs the webhooks belonging to the version a call ran on, and a draft is never
            that version.{" "}
            <span className="text-foreground font-medium">
              Until this agent is published, an installed hook fires on no call at all
            </span>
            . Publishing is separate because it also ships everything else in that draft — a
            half-written prompt included — to whoever rings next.
          </p>

          {plan?.draft.exists ? (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription className="space-y-3">
                <p>
                  Publishing{" "}
                  {plan.draft.version ? (
                    <span className="font-mono">{plan.draft.version}</span>
                  ) : (
                    "this draft"
                  )}{" "}
                  {plan.draft.agent_has_published_version
                    ? "replaces what live callers hear on this agent, and ships everything in the draft — not just the hook. This app cannot tell whose work is in there."
                    : "makes this agent live for the first time: it has never been published, which is how every new agent starts. If the prompt is still being written, finish it first."}
                </p>
                <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
                  <Switch checked={confirmPublish} onCheckedChange={setConfirmPublish} />
                  I have looked at the draft and it is safe to publish
                </label>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => void runPublish()}
              disabled={busy || Boolean(publishBlocked)}
              title={publishBlocked ?? undefined}
            >
              <ArrowUpRight className="size-4" />
              {publisher.isPending ? "Publishing…" : "Publish the agent"}
            </Button>
            {publishBlocked ? (
              <p className="text-muted-foreground text-xs">{publishBlocked}</p>
            ) : null}
          </div>
        </div>

        {published ? <InstallSteps result={published} /> : null}
      </CollapsibleSection>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------- the rows ---

/**
 * A box to tick, with the reason under it.
 *
 * The whole screen is these plus done-ticks, because Arun's complaint about the previous version
 * was exactly that it explained instead of asking: *"cant you just give me a simple checkbox for
 * the fdes to tick?"* The label is what will happen; the detail is why it matters, outside-in.
 *
 * The label is a real `<label>` wrapping the control, so the sentence is a hit target rather than
 * a 16-pixel square being the only way to answer the question.
 */
/**
 * Stop sending one hand-made context mapping.
 *
 * The only place this can be done now. These rows were created by an account-wide Context card
 * that Arun had removed — *"felds are diofferent for doffent agents"* — and the removal left
 * them running with no surface at all: still sent on every callback, still able to 422 a real
 * call when the dialled agent does not declare the name, and invisible except as a line of
 * prose. Showing a row while offering no way to act on it is the same defect the Outcomes tab
 * had with its "—" columns.
 *
 * Deliberately a confirm, and deliberately worded twice: an account-wide row is a different
 * blast radius from this agent's own, and the button cannot tell you which without saying so.
 */
function StopMapping({
  accountId,
  mapping,
  onDone,
}: {
  accountId: string;
  mapping: CodeActionPlan["context"][number];
  onDone: () => void;
}) {
  const del = useDeleteFieldMapping(accountId);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" className="shrink-0" disabled={del.isPending}>
          <MinusCircle className="size-3.5" />
          Stop sending
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Stop sending <span className="font-mono">{mapping.field_name}</span>?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {mapping.account_wide
              ? "This row has no agent on it, so it applies to every agent on this account — removing it stops the field being sent on all of their callbacks, not just this one's."
              : "Callbacks dialled on this agent stop carrying the field. Nothing else changes, and no other agent is affected."}{" "}
            Calls already queued keep the context they were built with. It cannot be undone from
            this screen.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() =>
              del.mutate(mapping.id, {
                onSuccess: () => {
                  toast.success(`No longer sending ${mapping.field_name}.`);
                  onDone();
                },
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
          >
            Stop sending it
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TickRow({
  id,
  checked,
  onChange,
  label,
  detail,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
  detail: React.ReactNode;
  /** For a box that is asked for unconditionally — ticked, and honest that it is not a choice. */
  disabled?: boolean;
}): React.ReactElement {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3",
        disabled ? "cursor-default" : "hover:bg-accent/40 cursor-pointer",
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-muted-foreground text-sm">{detail}</p>
      </div>
    </label>
  );
}

/**
 * Which of this agent's own fields plays a role.
 *
 * Arun: *"Select the field that will capture the call back date and time once the call is over /
 * select the field that closely matches this. if no field exists - show that there is no field
 * and indicate brief will ask for the field to be added"*.
 *
 * The app has always guessed — the name it would have created, then names seen on other tenants,
 * then a typed heuristic — and the guess is right on every tenant measured. What it could not do
 * was be overruled, so an agent with two plausible fields got whichever the rules reached first
 * and the only way round it was to rename a field on the tenant. The guess is now the default of
 * a picker, with its own reasoning under it, and the choice is remembered (migration 019).
 *
 * The empty state has no control at all: an agent with no candidate field has nothing to pick
 * from, and the honest thing to show is what the brief will ask for.
 */
function FieldPicker({
  id,
  label,
  empty,
  candidates,
  chosen,
  deliberatelyNone,
  why,
  note,
  busy,
  onChange,
  children,
}: {
  id: string;
  label: string;
  /** What is true, and what the brief will do about it, when there is nothing to pick. */
  empty: string;
  candidates: CodeActionPlan["fields"];
  chosen: string | null;
  /**
   * True when somebody has said this agent has no field for the role. Distinct from `chosen`
   * being null, which means the app found nothing — the picker must show the difference, or
   * choosing "none of these" looks like it did nothing because the guess comes back.
   */
  deliberatelyNone?: boolean;
  /** The app's own sentence about why this field, shown under the picker. */
  why?: string;
  /** A standing explanation of what this role is for, shown whether or not one is chosen. */
  note?: React.ReactNode;
  busy: boolean;
  /** A name, `""` for "this agent has none", or null to hand the choice back to the app. */
  onChange: (fieldName: string | null) => void;
  children?: React.ReactNode;
}): React.ReactElement {
  if (candidates.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-lg border p-3">
        <MinusCircle className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 space-y-2">
          <div className="space-y-1">
            <p className="text-sm font-medium">{label}</p>
            <p className="text-muted-foreground text-sm">{empty}</p>
          </div>
          {note ? <p className="text-muted-foreground text-xs">{note}</p> : null}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={chosen ?? "__none__"}
        disabled={busy}
        onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {/*
            First, not last. Arun: *"cant select none of these fields"* — it was at the bottom of
            a list of sixteen on a real agent, which is the same as not being there. The escape
            hatch belongs where somebody looking for it will find it.
          */}
          <SelectItem value="__none__" className="cursor-pointer">
            None of these — the brief asks for a new field
          </SelectItem>
          {candidates.map((f) => (
            <SelectItem key={f.name} value={f.name} className="cursor-pointer font-mono">
              {f.name}
              {f.type ? ` · ${f.type.toLowerCase()}` : ""}
              {f.allowed_values?.length ? ` · ${f.allowed_values.length} values` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        {chosen ? (
          why
        ) : deliberatelyNone ? (
          <>
            {empty}{" "}
            <button
              type="button"
              className="hover:text-foreground cursor-pointer underline underline-offset-2"
              onClick={() => onChange(null)}
            >
              Let the app pick again
            </button>
          </>
        ) : (
          empty
        )}
      </p>
      {note ? <p className="text-muted-foreground text-xs">{note}</p> : null}
      {children}
    </div>
  );
}

/**
 * "This prompt is missing a sentence" — never "this prompt is wrong".
 *
 * A conversation field's description *is* its extraction prompt, and this app used to compare it
 * byte-for-byte against its own wording and offer a button to replace it. equentis showed what
 * that meant: 1575 characters of careful, tenant-specific prompt — it resolves "tomorrow" and
 * "Saturday" against the call date, takes the earliest point of any range, lists its output
 * shapes in order — and the app was offering to delete all of it. Arun, having come close:
 * *"i pressed somerhing and somethign happened - now i am anxios abour waht got changed"*.
 *
 * What the app knows is narrower and worth saying: post-call analysis is given the date but not
 * the clock, so a prompt with no output shape for "in two minutes" returns nothing at all —
 * which cost a real callback. So it states the gap, states the failure, gives the sentence, and
 * the only control is a tick asking the brief to have somebody merge it in.
 */
function PromptCoverage({
  gaps,
  merge,
  onMerge,
}: {
  gaps: CodeActionPlan["description_gaps"];
  merge: boolean;
  onMerge: (v: boolean) => void;
}): React.ReactElement {
  if (gaps.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        Its wording covers every shape this app can read, including an offset from now (“in two
        minutes”) and which language to answer in.
      </p>
    );
  }
  return (
    <div className="border-warning/40 bg-warning/5 space-y-3 rounded-md border p-3">
      <p className="text-sm">
        Its wording looks like it is missing{" "}
        {gaps.length === 1 ? "an instruction" : `${gaps.length} instructions`} — and the
        description is the prompt post-call analysis reads, so this decides what actually lands in
        the field.
      </p>
      <ul className="space-y-3">
        {gaps.map((gap) => (
          <li key={gap.gap} className="space-y-1">
            <p className="text-sm font-medium">{gap.missing}</p>
            <p className="text-muted-foreground text-xs">{gap.why}</p>
            <p className="bg-background rounded border p-2 text-xs italic">
              &ldquo;{gap.add}&rdquo;
            </p>
          </li>
        ))}
      </ul>
      <label className="flex cursor-pointer items-start gap-3 text-sm select-none">
        <Checkbox checked={merge} onCheckedChange={(v) => onMerge(v === true)} className="mt-0.5" />
        <span className="text-muted-foreground">
          Ask the brief to merge{" "}
          {gaps.length === 1 ? "this sentence" : "these sentences"} into this agent's own wording.{" "}
          <span className="text-foreground">Nothing is replaced</span> — your Claude Code reads
          what is there, keeps it, and adds the missing shape in the same style.
        </span>
      </label>
    </div>
  );
}

/**
 * One of the tenant's own values, and what this account has decided it means.
 *
 * Their words on the left, an action on the right. The app guesses with an ordered set of
 * regexes — good enough that most rows need no attention, and wrong often enough that being
 * unable to correct it was the real defect: equentis has ten values and three the app cannot
 * place at all. Saved on change, because a select with its own save button is two decisions
 * where there is one.
 */
/*
  `ValueTick` lived here, and its whole history is worth one paragraph because the concept it
  drew is gone.

  It started as a four-way dropdown per value of the tenant's ENUM. Arun cut that to a checkbox
  ("why cant this be a checkbox instead of that dropdown?"), then cut the rows that could never
  be ticked ("why si that even na opton? … dont include them in the app"), and then asked the
  question that removed the field itself: *"do i need the fields that sys they asked for a
  calback? the call back field already tells us the info for when to call back."*

  It does. `CALLBACK_TIME_DESCRIPTION` asks post-call analysis for one of four exact answers, two
  of which are words — "requested no time given" and "not applicable" — so the callback-time
  field says both *whether* somebody asked and *when*. A second field asking the first question
  again was the double-asking he had already caught twice on this screen.

  So there is no value list, and nothing writes `disposition_value_overrides` any more. The
  server still honours a stored role and stored decisions if an account has them, so no script
  changes behind anyone's back — see discover.ts.
*/

/**
 * One ending the line itself reports, ticked or not, with the delay it would use.
 *
 * Drawn identically to `ValueTick` on purpose. These are account-wide and those are per-agent —
 * a real difference, and it is a footnote under the list rather than a reason to draw two lists.
 * Arun: *"why am i enabling these if user is saying i want these called back in the previosus
 * ecgtion"* — because the screen asked twice, once as a tick and once as a switch.
 */
function EndingTick({
  label,
  detail,
  checked,
  busy,
  delay,
  onChange,
}: {
  label: string;
  detail: string;
  checked: boolean;
  busy: boolean;
  delay: string | null;
  onChange: (on: boolean) => void;
}): React.ReactElement {
  const id = `ending-${label.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`;
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Checkbox
        id={id}
        checked={checked}
        disabled={busy}
        className="mt-0.5"
        onCheckedChange={(v) => onChange(v === true)}
      />
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="cursor-pointer font-normal">
          <span className="text-sm">{label}</span>
        </Label>
        <p className="text-muted-foreground text-xs">{detail}</p>
      </div>
      {checked && delay ? (
        <span className="text-muted-foreground shrink-0 text-xs">after {delay}</span>
      ) : null}
    </div>
  );
}


// --------------------------------------------------------------- a folded section ---

/**
 * A `SettingsSection` that starts folded, for a section you only open when you are doing the
 * thing it does.
 *
 * Arun: *"keep install the post call hook section minimused by default."* Installing is one
 * decision taken once, and the two big cards behind it pushed this agent's channels and context
 * off the bottom of the screen every time the page was opened.
 *
 * Local rather than a change to `SettingsSection`, which is vendored from the design system.
 * Same anatomy — recessed header band, divider, body — with three differences: the header is a
 * real `<button>` so it has the pointer, the hover and the focus ring a control must have; the
 * divider only exists when the body does, or a closed card shows a line under nothing; and the
 * collapsed header carries `summary` in place of `description`, because a fold that hides its own
 * state is just a thing to click twice.
 */
function CollapsibleSection({
  title,
  description,
  summary,
  children,
}: {
  title: string;
  /** Shown while open: what the body is for. */
  description: string;
  /** Shown while closed: what the body would have told you. */
  summary: string;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <section className="bg-card text-card-foreground overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "bg-muted/40 hover:bg-muted/70 focus-visible:ring-ring/50 flex w-full cursor-pointer items-start gap-3 px-6 py-4 text-left transition-colors focus-visible:ring-[3px] focus-visible:outline-none focus-visible:-outline-offset-2",
          open && "border-b",
        )}
      >
        <ChevronRight
          className={cn(
            "text-muted-foreground mt-1 size-4 shrink-0 transition-transform",
            open && "rotate-90",
          )}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          <p className="text-muted-foreground mt-1 text-sm">{open ? description : summary}</p>
        </div>
        <span className="text-muted-foreground mt-0.5 shrink-0 text-xs">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open ? <div className="space-y-4 px-6 py-5">{children}</div> : null}
    </section>
  );
}

// --------------------------------------------------------------- the results ---

function InstallSteps({ result }: { result: CodeActionInstallResponse }): React.ReactElement {
  return (
    <div className="space-y-3 rounded-md border p-4">
      {/* A dry run is neither good news nor bad — it is a description. Colouring it green
          for "ok" made a run that wrote nothing look like a finished install. */}
      <p
        className={cn(
          "text-sm",
          result.dryRun ? "text-foreground" : result.ok ? "text-success" : "text-destructive",
        )}
      >
        {result.message}
      </p>
      <ul className="space-y-2">
        {result.steps.map((s, i) => (
          <li key={`${s.step}-${i}`} className="flex gap-2 text-sm">
            {STEP_ICON[s.status]}
            <span>
              {s.step}
              <span className="text-muted-foreground block text-xs">{s.detail}</span>
            </span>
          </li>
        ))}
      </ul>
      {result.verified ? (
        <p className="text-muted-foreground text-xs">
          Read back from the active version:{" "}
          {result.verified.on_active_version
            ? `webhook ${result.verified.webhook_id} is live on ${result.verified.version}.`
            : "the webhook is NOT on the active version, so it will not fire."}
        </p>
      ) : null}
    </div>
  );
}

function TestResult({ run }: { run: CodeActionTestResponse }): React.ReactElement {
  const captured = Object.entries(run.result.captured_field_values ?? {});
  return (
    <div className="space-y-3 rounded-md border p-4">
      <p className={cn("text-sm", run.result.success ? "text-success" : "text-destructive")}>
        {run.message}
      </p>
      <p className="text-muted-foreground text-xs">
        Ran in {run.result.duration_ms} ms against <span className="font-mono">{run.ingest_url}</span>
        .
      </p>
      {captured.length > 0 ? (
        <div className="text-xs">
          <span className="font-medium">Field values it would write back onto the call</span>
          <ul className="mt-1 space-y-0.5">
            {captured.map(([k, v]) => (
              <li key={k}>
                <span className="font-mono">{k}</span> = <span className="font-mono">{String(v)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {(run.result.logs ?? []).length > 0 ? (
        <pre className="bg-muted/40 max-h-64 overflow-auto rounded p-3 text-xs whitespace-pre-wrap">
          {(run.result.logs ?? [])
            .map((l) => `${(l.level ?? "info").padEnd(5)} ${l.message ?? ""}`)
            .join("\n")}
        </pre>
      ) : null}
    </div>
  );
}
